import { isPlatformBrowser } from '@angular/common';
import {
	Component,
	computed,
	inject,
	input,
	output,
	PLATFORM_ID,
	signal,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';

import { TranslatePipe } from '@ngx-translate/core';
import { MarkdownModule } from 'ngx-markdown';
import {
	catchError,
	defer,
	distinctUntilChanged,
	EMPTY,
	forkJoin,
	map,
	Observable,
	of,
	shareReplay,
	switchMap,
	take,
	tap,
} from 'rxjs';

import { CheckboxComponent } from '@valk-nx/components/ui-checkbox/src/lib/checkbox';
import { ModalComponent } from '@valk-nx/components/ui-modal/src/lib/modal';
import { TabMenuComponent } from '@valk-nx/components/ui-tab-menu/src/lib/tab-menu';
import { TabMenuItemDirective } from '@valk-nx/components/ui-tab-menu/src/lib/tab-menu.directive';
import { CookieBannerService } from '@valk-nx/services/cookie-manager/src/lib/cookie-banner.service';
import {
	CookieKey,
	Status,
} from '@valk-nx/services/cookie-manager/src/lib/cookie-manager.interface';

import {
	COOKIE_KEYS,
	CookieAction,
	CookiePreferences,
} from './cookie-banner.interface';

@Component({
	selector: 'vp-cookie-banner',
	templateUrl: './cookie-banner.html',
	imports: [
		ModalComponent,
		TranslatePipe,
		TabMenuComponent,
		TabMenuItemDirective,
		CheckboxComponent,
		MarkdownModule,
	],
})
export class CookieBannerComponent {
	readonly activeTab = signal<number>(0);

	private readonly cookieBannerService = inject(CookieBannerService);
	private readonly activatedRoute = inject(ActivatedRoute);
	private readonly router = inject(Router);
	private readonly platformId = inject(PLATFORM_ID);
	private readonly isBrowser = isPlatformBrowser(this.platformId);

	language = input<string>('nl');

	private readonly language$ = toObservable(this.language);

	private readonly legalContent$ = this.language$.pipe(
		distinctUntilChanged(), // Prevent duplicate calls for same language
		switchMap((lang) => this.cookieBannerService.getLegal(lang)),
		shareReplay({ bufferSize: 1, refCount: true }), // Cache & cleanup
		catchError((error) => {
			console.error('Failed to load legal content:', error);
			return of(null);
		}),
	);

	// Convert back to signal for template usage
	readonly legalContent = toSignal(this.legalContent$, {
		initialValue: null,
	});

	private readonly urlFragment = toSignal(this.activatedRoute.fragment, {
		initialValue: null,
	});

	private readonly shouldShowFromUrl = computed(
		() => this.urlFragment() === 'showcookies',
	);

	readonly showModal = computed(
		() => this.shouldShowFromUrl() || this.manualShowModal(),
	);

	readonly manualShowModal = signal(false);

	readonly cookieValues = signal<CookiePreferences>({
		'valk-functional-cookie': true,
		'valk-analytics-cookie': false,
		'valk-marketing-cookie': false,
	});

	cookiesSelection = output<CookiePreferences>();

	private readonly cookiePreferences$ = defer(() => {
		// Load from API
		return forkJoin(
			COOKIE_KEYS.map((key) =>
				this.cookieBannerService.getCookie(key).pipe(
					map((status) => ({
						key,
						value: status === Status.ACCEPTED,
						isSet: status !== Status.EMPTY,
					})),
				),
			),
		).pipe(
			map((results) => {
				// Build preferences object with functional cookie always true
				const preferences = results.reduce(
					(acc, { key, value }) => ({
						...acc,
						[key]: key === 'valk-functional-cookie' ? true : value,
					}),
					{} as CookiePreferences,
				);

				const allSet = results.every((r) => r.isSet);

				return { preferences, allSet };
			}),
			tap(({ preferences, allSet }) => {
				// Update signal with loaded preferences
				this.cookieValues.set(preferences);
				this.manualShowModal.set(!allSet);
			}),
			shareReplay(1),
		);
	}).pipe(take(1));

	constructor() {
		// Auto-subscribe to load preferences (cleanup via take(1))
		this.cookiePreferences$.subscribe();
	}

	onCloseModal() {
		this.manualShowModal.set(false);
	}

	showAboutTab() {
		this.activeTab.set(2); // About is the 3rd tab (index 2)
	}

	setCookieValue(cookie: CookieKey, value: boolean): void {
		this.cookieValues.update((current) => ({
			...current,
			// Functional cookie cannot be disabled
			[cookie]: cookie === 'valk-functional-cookie' ? true : value,
		}));
	}

	handleCookies(action: CookieAction) {
		// Auto-subscribe with take(1) for cleanup
		this.saveCookiePreferences(action).subscribe();
	}

	private calculateCookieValues(action: CookieAction): CookiePreferences {
		switch (action) {
			case CookieAction.AcceptAll:
				return {
					'valk-functional-cookie': true,
					'valk-analytics-cookie': true,
					'valk-marketing-cookie': true,
				};
			case CookieAction.RejectAll:
				return {
					'valk-functional-cookie': true,
					'valk-analytics-cookie': false,
					'valk-marketing-cookie': false,
				};
			case CookieAction.Selection:
				return {
					...this.cookieValues(),
					'valk-functional-cookie': true, // Always enforce
				};
		}
	}

	private saveCookiePreferences(
		action: CookieAction,
	): Observable<CookiePreferences> {
		const newValues = this.calculateCookieValues(action);
		const cookieKeys = Object.keys(newValues) as CookieKey[];

		return forkJoin(
			cookieKeys.map((key) =>
				this.cookieBannerService.setCookie(
					key,
					newValues[key] ? Status.ACCEPTED : Status.DECLINED,
				),
			),
		).pipe(
			map(() => newValues),
			tap((values) => {
				this.cookieValues.set(values);
				this.cookiesSelection.emit(values);
				this.manualShowModal.set(false);
				this.removeHash();
			}),
			catchError((error) => {
				console.error('Failed to save cookie preferences:', error);
				return EMPTY;
			}),
			take(1),
		);
	}

	removeHash() {
		if (this.isBrowser && globalThis.location.hash === '#showcookies') {
			this.router.navigate([], { fragment: undefined, replaceUrl: true });
		}
	}
}
